import { useCallback, useState } from "react";
import { GameState, QuizQuestion, QuizStats, UserAnswer } from "../types/quiz";

export const useQuizGame = (questions: QuizQuestion[]) => {
  const [gameState, setGameState] = useState<GameState>({
    currentQuestionIndex: 0,
    score: 0,
    answers: [],
    isComplete: false,
    startTime: Date.now(),
  });

  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const currentQuestion = questions[gameState.currentQuestionIndex];

  const handleAnswer = useCallback(
    (optionId: string) => {
      if (selectedOption || showExplanation) return;

      const timeSpent = Date.now() - questionStartTime;
      const option = currentQuestion.options.find((opt) => opt.id === optionId);

      if (!option) return;

      const answer: UserAnswer = {
        questionId: currentQuestion.id,
        selectedOptionId: optionId,
        isCorrect: option.isCorrect,
        timeSpent,
      };

      setSelectedOption(optionId);
      setShowExplanation(true);

      setGameState((prev) => ({
        ...prev,
        score: prev.score + (option.isCorrect ? 1 : 0),
        answers: [...prev.answers, answer],
      }));
    },
    [selectedOption, showExplanation, questionStartTime, currentQuestion],
  );

  const nextQuestion = useCallback(() => {
    const nextIndex = gameState.currentQuestionIndex + 1;

    if (nextIndex >= questions.length) {
      setGameState((prev) => ({
        ...prev,
        isComplete: true,
        endTime: Date.now(),
      }));
    } else {
      setGameState((prev) => ({
        ...prev,
        currentQuestionIndex: nextIndex,
      }));
      setSelectedOption(null);
      setShowExplanation(false);
      setQuestionStartTime(Date.now());
    }
  }, [gameState.currentQuestionIndex, questions.length]);

  const resetGame = useCallback(() => {
    setGameState({
      currentQuestionIndex: 0,
      score: 0,
      answers: [],
      isComplete: false,
      startTime: Date.now(),
    });
    setSelectedOption(null);
    setShowExplanation(false);
    setQuestionStartTime(Date.now());
  }, []);

  const getStats = useCallback((): QuizStats => {
    const totalTime = (gameState.endTime || Date.now()) - gameState.startTime;
    const correctAnswers = gameState.answers.filter((a) => a.isCorrect).length;
    const totalQuestions = questions.length;

    return {
      totalQuestions,
      correctAnswers,
      wrongAnswers: totalQuestions - correctAnswers,
      accuracy: (correctAnswers / totalQuestions) * 100,
      totalTime,
      averageTimePerQuestion: totalTime / totalQuestions,
      scorePercentage: (correctAnswers / totalQuestions) * 100,
    };
  }, [gameState, questions.length]);

  return {
    gameState,
    currentQuestion,
    selectedOption,
    showExplanation,
    handleAnswer,
    nextQuestion,
    resetGame,
    getStats,
    progress: ((gameState.currentQuestionIndex + 1) / questions.length) * 100,
  };
};
